import {Injectable, Inject, inject} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import {currentContext, Llm_response, Message} from '@llm/models/status_interaction';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";

@Injectable({
  providedIn: 'root'
})
export class BotResponseService {
  //Injectables
  private _console = inject(Tools_aiConsoleService);
  private chatHistory: Message[] = [];
  private firstResponseReceived = false;
  private botResponseSubject = new BehaviorSubject<string>('');

  constructor(@Inject(DOCUMENT) private document: Document) {}

  public botResponse$: Observable<string> = this.botResponseSubject.asObservable();

  // Método principal para manejar respuestas
  public handleResponse(
    response: Llm_response | string | null,
    botAvatar: string,
    chatsElement?: HTMLElement
  ): void {
    if (!response) {
      this.handleNullResponse(botAvatar, chatsElement);
      return;
    }

    if (typeof response === 'string') {
      this.handleStringResponse(response, botAvatar, chatsElement);
    } else {
      this.handleObjectResponse(response, botAvatar, chatsElement);
    }
  }

  private handleNullResponse(botAvatar: string, chatsElement?: HTMLElement): void {
    const fallbackMsg = `Aconteceu um error desconocido: null response`;
    const botResponse = this.generateBotResponse(fallbackMsg, botAvatar);
    this.updateChatUI(botResponse, chatsElement);
    this.chatHistory.push({ role: 'assistant', content: 'null response' });
  }

  private handleStringResponse(response: string, botAvatar: string, chatsElement?: HTMLElement): void {
    const sanitizedResponse = this.sanitizeResponse(response);

    if (!this.firstResponseReceived) {
      const botResponse = this.generateBotResponse(sanitizedResponse, botAvatar);
      this.updateChatUI(botResponse, chatsElement);
      this.firstResponseReceived = true;
    } else {
      this.updateLastMessage(sanitizedResponse);
    }

    this.botResponseSubject.next(sanitizedResponse);
  }

  private handleObjectResponse(response: Llm_response, botAvatar: string, chatsElement?: HTMLElement): void {
    const fallbackMsg = response.voice_message || 'Resposta do assistente';
    const botResponse = this.generateBotResponse(fallbackMsg, botAvatar);
    this.updateChatUI(botResponse, chatsElement);
    this.chatHistory.push({ role: 'assistant', content: response.toString() });
    this.botResponseSubject.next(fallbackMsg);
  }

  private generateBotResponse(content: string, botAvatar: string): string {
    return `
      <img class="botAvatar" src="${botAvatar}" alt="bot profile"/>
      <p class="botMsg">${content}</p>
      <div class="clearfix"></div>
    `;
  }

  private sanitizeResponse(response: string): string {
    return response.replace(/["{}:]/g, '');
  }

  private updateChatUI(responseHtml: string, chatsElement?: HTMLElement): void {
    if (chatsElement) {
      chatsElement.insertAdjacentHTML('beforeend', responseHtml);
    }
  }

  private updateLastMessage(content: string): void {
    const botMessages = this.document.getElementsByClassName('botMsg');
    if (botMessages.length > 0) {
      botMessages[botMessages.length - 1].textContent = content;
    }
  }

  public getChatHistory(): Message[] {
    return [...this.chatHistory];
  }

  public resetChatState(): void {
    this.chatHistory = [];
    this.firstResponseReceived = false;
  }
}
